import React from "react";
import s from "./styles.module.scss";

const PrivacyPolicy = () => {
  return (
    <div className={s.legalPageContainer}>
      <div className={s.boxContent}>
        <div className={s.legalContent}>
          <h1 className={s.legalTitle}>Privacy Policy</h1>
          <p className={s.legalEffectiveDate}>
            <strong>Effective Date:</strong> August 25th, 2025
          </p>

          <p className={s.legalIntro}>
            Welcome to Ocean ("we," "us," or "our"). Ocean is a web application
            that helps students organize their academic schedules by extracting
            events from uploaded syllabi and integrating them into their
            personal calendars.
          </p>
          <p className={s.legalIntro}>
            We respect your privacy and are committed to protecting the personal
            information you share with us. This Privacy Policy explains what
            data we collect, why we collect it, how we use it, and your rights
            regarding your data.
          </p>

          <div className={s.legalSection}>
            <h2>1. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            <ul>
              <li>
                <strong>Uploaded Content:</strong> When you upload a syllabus
                (PDF, DOCX), we process its content solely to extract academic
                events (such as class meetings, assignments, and exams).
              </li>
              <li>
                <strong>Section Selections:</strong> We store your selected
                course sections to personalize your event extraction.
              </li>
              <li>
                <strong>Calendar Access:</strong> If you connect a calendar
                (Google Calendar, Outlook Calendar), we use OAuth tokens to
                securely request permission to insert events on your behalf.
              </li>
              <li>
                <strong>Technical Information:</strong> We may collect basic
                technical details like browser type and device (non-personal
                data) to improve the user experience.
              </li>
            </ul>
          </div>

          <div className={s.legalSection}>
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Extract academic events from syllabi you provide.</li>
              <li>
                Insert events into your selected calendar (only with your
                explicit permission).
              </li>
              <li>Improve and personalize your experience with Ocean.</li>
            </ul>
            <p>
              We <strong>do not</strong> access, store, or modify any existing
              calendar events.
            </p>
          </div>

          <div className={s.legalSection}>
            <h2>3. Data Retention and Deletion</h2>
            <ul>
              <li>
                Uploaded syllabi are temporarily stored only during the
                extraction process and are deleted afterward.
              </li>
              <li>
                OAuth tokens are securely handled and expire automatically or
                can be revoked by you at any time. We do not permanently store
                personal data unless explicitly stated.
              </li>
            </ul>
            <p>
              You can request data deletion at any time by contacting us at{" "}
              <a href="mailto:info@oceanwebapp.com">info@oceanwebapp.com</a>.
            </p>
          </div>

          <div className={s.legalSection}>
            <h2>4. Sharing of Information</h2>
            <p>
              We <strong>do not</strong> sell, rent, or share your personal data
              with third parties.
            </p>
            <p>
              We only share data as required to operate the service (for
              example, making authorized API calls to Google or Microsoft
              Calendar based on your consent).
            </p>
            <p>
              <strong>Legal Compliance / Law-Enforcement Data Requests:</strong> We disclose user information only when legally required (e.g., subpoena, search warrant). 
              Requests must be sent to{" "} <a href="mailto:legal@oceanwebapp.com">legal@oceanwebapp.com</a> or our registered agent (address below). 
              Each request is reviewed by counsel, we limit disclosure to the minimum required data, and—unless prohibited by law or emergency—we notify affected users before producing information.
            </p>
            <p>
            <strong>Registered Agent:</strong> <br />Ocean Technologies LLC<br />
            c/o Harvard Business Services<br />
            16192 Coastal Highway<br />
            Lewes, DE 19958<br />
            United States
          </p>
          </div>

          <div className={s.legalSection}>
            <h2>5. Security</h2>
            <p>
              We use industry-standard measures (including HTTPS encryption) to
              protect your information. While we work to ensure the security of
              your data, no system can be guaranteed 100% secure.
            </p>
          </div>

          <div className={s.legalSection}>
            <h2>6. Your Rights</h2>
            <ul>
              <li>
                Revoke calendar access at any time through your Google or
                Microsoft account settings.
              </li>
              <li>
                Request deletion of any stored data by contacting{" "}
                <a href="mailto:info@oceanwebapp.com">info@oceanwebapp.com</a>.
              </li>
              <li>Contact us with questions or concerns about this policy.</li>
              <li>
                Submit a privacy complaint or dispute by contacting{" "}
                <a href="mailto:info@oceanwebapp.com">info@oceanwebapp.com</a>.{" "}
                All complaints are handled in accordance with our internal Privacy Complaint & Dispute Resolution Procedure, and we aim to resolve or escalate them within 30 days.
              </li>
            </ul>
          </div>

          <div className={s.legalSection}>
            <h2>7. Responsible AI Statement</h2>
              <p> 
                Ocean uses artificial-intelligence tools to make scheduling easier, and we are committed to keeping those tools safe, fair, and reliable.
              </p>
          </div>

          <div className={s.legalSection}>
            <h2>8. Children's Privacy</h2>
            <p>
              Ocean is not intended for children under the age of 13, and we do
              not knowingly collect personal information from children under 13.
            </p>
            <p>
              If we discover that a child under 13 has provided us with personal
              information, we will promptly delete such information.
            </p>
            <p>
              If you believe that we might have any information from or about a
              child under 13, please contact us at{" "}
              <a href="mailto:info@oceanwebapp.com">info@oceanwebapp.com</a>.
            </p>
          </div>

          <div className={s.legalSection}>
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Material
              changes will be posted on this page with an updated effective
              date.
            </p>
          </div>

          <div className={s.legalSection}>
            <h2>10. Contact Us</h2>
            <p>If you have any questions or concerns, please contact us at:</p>
            <p>
              <a href="mailto:info@oceanwebapp.com">info@oceanwebapp.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
